import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { lockBodyScroll } from '@platform/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import type { NavigationItem } from '@/shared/types/layout.ts';
import { navigationItems } from '@/shared/constants/app/navigationItems.ts';
import { useAuth, useLogout } from '@/features/auth';
import {
  AppHeaderLogo,
  HeaderUserActions,
  ThemeToggle,
} from '@/shared/components/layout';

/** 이만큼 내려가면 헤더가 불투명해진다. */
const HEADER_SOLID_AFTER = 24;

/**
 * 구매 진입점. 헤더에서 유일하게 채워진 버튼이어야 한다.
 * `#CD0000` 은 흰 글씨 대비 6.37:1(AA). `#FA0F00` 은 3.6:1 이라 쓰면 안 된다.
 */
const CTA_CLASS =
  'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-[#CD0000] ' +
  'font-semibold text-white transition-[background-color,transform] duration-150 ' +
  'hover:-translate-y-px hover:bg-[#AC0000] ' +
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#CD0000]';

export function Header() {
  const [activeId, setActiveId] = useState<number | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const closeTimer = useRef<number | null>(null);
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { mutate: logout } = useLogout();

  // 라우트 변경 시 메뉴 닫기 — useDerivedState 패턴.
  // useEffect + setState 조합보다 한 렌더 빠르고, set-state-in-effect 룰도 우회.
  // (route key 가 바뀐 *그 렌더* 에 바로 reset → effect 의 비동기 갭이 없음)
  const locationKey = `${location.pathname}#${location.hash}`;
  const [lastLocationKey, setLastLocationKey] = useState(locationKey);
  if (lastLocationKey !== locationKey) {
    setLastLocationKey(locationKey);
    setActiveId(null);
    setMobileOpen(false);
  }

  // 홈 최상단에서만 투명. 메가메뉴·드로어가 열리면 흰 패널이 붙으므로 즉시 불투명.
  const isOverlayRoute = location.pathname === PATHS.HOME;
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    // 홈을 스크롤한 채 떠났다 돌아오면 scrolled 가 참으로 남아서, 라우트마다 다시 맞춘다.
    const sync = () =>
      setScrolled(isOverlayRoute && window.scrollY > HEADER_SOLID_AFTER);
    sync();
    if (!isOverlayRoute) return;
    window.addEventListener('scroll', sync, { passive: true });
    return () => window.removeEventListener('scroll', sync);
  }, [isOverlayRoute]);

  const solid = !isOverlayRoute || scrolled || mobileOpen || activeId !== null;

  // ESC로 닫기
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveId(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // 외부 클릭 닫기
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!headerRef.current?.contains(e.target as Node)) setActiveId(null);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // 모바일 메뉴가 열려 있는 동안 뒤 페이지 스크롤 막기
  useEffect(() => {
    if (!mobileOpen) return;
    return lockBodyScroll();
  }, [mobileOpen]);

  const handleToggle = (item: NavigationItem) => {
    if (!item.groups) return;
    setActiveId((prev) => (prev === item.id ? null : item.id));
  };

  const scheduleClose = () => {
    closeTimer.current = window.setTimeout(() => setActiveId(null), 120);
  };
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const activeItem = navigationItems.find((n) => n.id === activeId && n.groups);

  return (
    <header
      ref={headerRef}
      className={`fixed inset-x-0 top-0 z-50 ${solid ? '' : 'dark'}`}
      onMouseLeave={scheduleClose}
      onMouseEnter={cancelClose}
    >
      {/* 상단 바 */}
      <nav
        className={`relative border-b transition-colors duration-300 ${
          solid
            ? 'border-black/[0.08] bg-white/80 backdrop-blur-xl backdrop-saturate-150 dark:border-white/10 dark:bg-dark-base/80'
            : 'border-transparent bg-transparent'
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1440px] items-center gap-10 px-5 sm:px-8">
          <AppHeaderLogo />

          {/* 데스크톱 메인 메뉴 */}
          <div className="hidden flex-1 items-center gap-1 md:flex">
            {navigationItems.map((item) => {
              const hasMenu = !!item.groups;
              const isActive = activeId === item.id;

              return hasMenu ? (
                <button
                  key={item.id}
                  type="button"
                  aria-expanded={isActive}
                  aria-controls={`mega-${item.id}`}
                  onClick={() => handleToggle(item)}
                  onMouseEnter={() => {
                    cancelClose();
                    setActiveId(item.id);
                  }}
                  className={`group relative inline-flex items-center gap-1 rounded-md px-3 py-2 text-[15px] font-medium transition-colors ${
                    isActive
                      ? 'text-neutral-950 dark:text-white'
                      : 'text-neutral-950/60 hover:text-neutral-950 dark:text-white/60 dark:hover:text-white'
                  }`}
                >
                  {item.label}
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 10 10"
                    className={`transition-transform duration-200 ${isActive ? 'rotate-180' : ''}`}
                  >
                    <path
                      d="M2 3.5L5 6.5L8 3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill="none"
                    />
                  </svg>
                  <span
                    className={`absolute inset-x-3 -bottom-px h-px bg-neutral-950 transition-opacity dark:bg-white ${
                      isActive ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                </button>
              ) : (
                <NavLink
                  key={item.id}
                  to={item.path}
                  onMouseEnter={() => setActiveId(null)}
                  className={({ isActive: navActive }) =>
                    `rounded-md px-3 py-2 text-[15px] font-medium transition-colors ${
                      navActive
                        ? 'text-neutral-950 dark:text-white'
                        : 'text-neutral-950/60 hover:text-neutral-950 dark:text-white/60 dark:hover:text-white'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}

            {/* 메뉴 오른쪽 끝에 붙는 구매 진입점. md 미만에선 액션 존 쪽이 대신 뜬다. */}
            <Link
              to={PATHS.STORE}
              onMouseEnter={() => setActiveId(null)}
              className={`${CTA_CLASS} ml-2 px-[18px] py-2 text-[15px]`}
            >
              구매하기
            </Link>
          </div>

          {/* 액션 존. 구매하기는 내비게이션이 아니라 행동이라 이쪽에 둔다. */}
          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            <HeaderUserActions />
            {/* 좁은 폭 전용. 여기서 지우면 결제 진입점이 햄버거 뒤로 숨는다. */}
            <Link
              to={PATHS.STORE}
              onMouseEnter={() => setActiveId(null)}
              className={`${CTA_CLASS} px-3.5 py-1.5 text-[13px] md:hidden`}
            >
              구매하기
            </Link>

            {/* 햄버거 (모바일) */}
            <button
              type="button"
              aria-label="메뉴 열기"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="relative grid h-10 w-10 place-items-center rounded-md text-neutral-950 transition-colors hover:bg-black/[0.04] dark:hover:bg-white/10 dark:text-white dark:hover:bg-white/10 md:hidden"
            >
              <span className="sr-only">메뉴</span>
              <span
                className={`absolute h-[1.5px] w-5 rounded-full bg-current transition-all duration-300 ${
                  mobileOpen ? 'rotate-45' : '-translate-y-[5px]'
                }`}
              />
              <span
                className={`absolute h-[1.5px] w-5 rounded-full bg-current transition-all duration-300 ${
                  mobileOpen ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <span
                className={`absolute h-[1.5px] w-5 rounded-full bg-current transition-all duration-300 ${
                  mobileOpen ? '-rotate-45' : 'translate-y-[5px]'
                }`}
              />
            </button>
          </div>
        </div>

        {/* 데스크톱 메가메뉴 */}
        <div
          id={activeItem ? `mega-${activeItem.id}` : undefined}
          className={`absolute inset-x-0 top-full hidden overflow-hidden border-b border-black/[0.08] bg-white/95 backdrop-blur-xl transition-[max-height,opacity] duration-300 ease-out dark:border-white/10 dark:bg-dark-surface/95 md:block ${
            activeItem ? 'max-h-[640px] opacity-100' : 'pointer-events-none max-h-0 opacity-0'
          }`}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {activeItem && (
            <div
              className={`mx-auto grid max-w-[1440px] gap-12 px-5 py-12 sm:px-8 ${
                activeItem.promo ? 'lg:grid-cols-[1fr_360px]' : ''
              }`}
            >
              <div
                className="grid gap-x-12 gap-y-10"
                style={{
                  gridTemplateColumns: `repeat(${Math.min(activeItem.groups!.length, 3)}, minmax(0, 1fr))`,
                }}
              >
                {activeItem.groups!.map((group) => (
                  <div key={group.id}>
                    {group.title && (
                      <h4 className="mb-5 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-950/40 dark:text-white/40">
                        {group.title}
                      </h4>
                    )}
                    <ul className="flex flex-col gap-1">
                      {group.items.map((sub) => (
                        <li key={sub.id}>
                          <Link
                            to={sub.path}
                            target={sub.target}
                            rel={sub.target === '_blank' ? 'noopener noreferrer' : undefined}
                            className="group/sub -mx-3 block rounded-xl px-3 py-2.5 transition-colors hover:bg-black/[0.035] dark:hover:bg-white/[0.06]"
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-[15px] font-semibold text-neutral-950 dark:text-white">
                                {sub.label}
                              </span>
                              {sub.badge && (
                                <span
                                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold tracking-wider ${
                                    sub.badge === 'NEW'
                                      ? 'bg-primary/10 text-primary'
                                      : sub.badge === 'HOT'
                                        ? 'bg-red-500/10 text-red-500'
                                        : 'bg-neutral-950/10 text-neutral-950 dark:bg-white/15 dark:text-white'
                                  }`}
                                >
                                  {sub.badge}
                                </span>
                              )}
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                className="ml-auto -translate-x-1 text-neutral-950/30 opacity-0 transition-all group-hover/sub:translate-x-0 group-hover/sub:opacity-100 dark:text-white/30"
                              >
                                <path
                                  d="M5 3L9 7L5 11"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  fill="none"
                                />
                              </svg>
                            </div>
                            {sub.description && (
                              <p className="mt-0.5 text-[13px] leading-[1.5] text-neutral-950/55 dark:text-white/55">
                                {sub.description}
                              </p>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {activeItem.promo && (
                <aside
                  className="relative overflow-hidden text-white"
                  style={{
                    backgroundImage: `linear-gradient(135deg, #0a0a0a 0%, #0a0a0a 140%)`,
                  }}
                >
                  <Link to={PATHS.STORE}>
                    <img
                      src={activeItem.promo.imageUrl}
                      alt="Sales move"
                      className="w-full object-cover"
                      loading="lazy"
                    />
                  </Link>
                  <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
                  <div className="relative p-6">
                    <h5 className="text-[18px] font-bold -tracking-[0.02em]">
                      {activeItem.promo.title}
                    </h5>
                    <p className="mt-2 text-[13px] leading-[1.6] text-white/75">
                      {activeItem.promo.description}
                    </p>
                    <Link
                      to={activeItem.promo.ctaPath}
                      className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[13px] font-semibold text-neutral-950 transition-transform hover:scale-[1.02]"
                    >
                      {activeItem.promo.ctaLabel}
                      <svg width="12" height="12" viewBox="0 0 12 12">
                        <path
                          d="M4 2.5L7.5 6L4 9.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          fill="none"
                        />
                      </svg>
                    </Link>
                  </div>
                </aside>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* 데스크톱 백드롭 */}
      <div
        onClick={() => setActiveId(null)}
        className={`fixed inset-x-0 top-16 bottom-0 -z-10 hidden bg-neutral-950/30 backdrop-blur-sm transition-opacity duration-300 md:block ${
          activeItem ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />

      {/* 모바일 드로어 */}
      <div
        className={`fixed inset-x-0 top-16 bottom-0 overflow-y-auto bg-white transition-[opacity,transform] duration-300 dark:bg-dark-base md:hidden ${
          mobileOpen ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
        }`}
      >
        <nav className="flex flex-col px-5 py-6">
          <Link
            to={PATHS.STORE}
            onClick={() => setMobileOpen(false)}
            className={`${CTA_CLASS} mb-4 w-full px-5 py-3.5 text-[16px]`}
          >
            구매하기
          </Link>
          {navigationItems.map((item) => (
            <MobileNavItem
              key={item.id}
              item={item}
              onNavigate={() => setMobileOpen(false)}
            />
          ))}
          {isAuthenticated ? (
            <button
              type="button"
              onClick={() => {
                logout();
                setMobileOpen(false);
              }}
              className="mt-2 border-t border-black/[0.06] py-4 text-left text-[16px] font-semibold text-neutral-950/70 dark:border-white/10 dark:text-white/70"
            >
              로그아웃
            </button>
          ) : (
            <Link
              to={PATHS.LOGIN}
              state={{ from: location }}
              onClick={() => setMobileOpen(false)}
              className="mt-2 border-t border-black/[0.06] py-4 text-[16px] font-semibold text-neutral-950/70 dark:border-white/10 dark:text-white/70"
            >
              로그인
            </Link>
          )}

          {/* 테마 토글 (모바일) */}
          <div className="mt-2 flex items-center justify-between border-t border-black/[0.06] py-4 dark:border-white/10">
            <span className="text-[16px] font-semibold text-neutral-950/70 dark:text-white/70">
              화면 테마
            </span>
            <ThemeToggle />
          </div>
        </nav>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────

interface MobileNavItemProps {
  item: NavigationItem;
  onNavigate: () => void;
}

function MobileNavItem({ item, onNavigate }: MobileNavItemProps) {
  const [open, setOpen] = useState(false);
  const showGroupTitles = (item.groups?.length ?? 0) > 1;

  if (!item.groups) {
    return (
      <Link
        to={item.path}
        onClick={onNavigate}
        className="border-b border-black/[0.06] py-4 text-[18px] font-semibold text-neutral-950 dark:border-white/10 dark:text-white"
      >
        {item.label}
      </Link>
    );
  }

  return (
    <div className="border-b border-black/[0.06] dark:border-white/10">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-4 text-left text-[18px] font-semibold text-neutral-950 dark:text-white"
      >
        {item.label}
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <path
            d="M3 5L7 9L11 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </button>
      <div
        className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-out ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="min-h-0">
          {/* 그룹이 둘 이상이면 제목을 살린다 — 평탄화하면 성격이 다른 항목이 한 줄기로 붙는다. */}
          <div className="flex flex-col gap-1 pb-4 pl-1">
            {item.groups.map((group) => (
              <div key={group.id} className="flex flex-col gap-1">
                {group.title && showGroupTitles && (
                  <h5 className="mt-2 px-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-neutral-950/40 dark:text-white/40">
                    {group.title}
                  </h5>
                )}
                {group.items.map((sub) => (
                  <Link
                    key={sub.id}
                    to={sub.path}
                    target={sub.target}
                    rel={sub.target === '_blank' ? 'noopener noreferrer' : undefined}
                    onClick={onNavigate}
                    className="rounded-lg py-2.5 text-[15px] font-medium text-neutral-950/70 active:bg-black/[0.04] dark:text-white/70 dark:active:bg-white/10"
                  >
                    {sub.label}
                    {sub.badge && (
                      <span
                        className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                          sub.badge === 'NEW'
                            ? 'bg-primary/10 text-primary'
                            : sub.badge === 'HOT'
                              ? 'bg-red-500/10 text-red-500'
                              : 'bg-neutral-950/10 text-neutral-950 dark:bg-white/15 dark:text-white'
                        }`}
                      >
                        {sub.badge}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
